<script setup lang="ts">
import { ref } from 'vue'

const emit = defineEmits<{ (e: 'close'): void }>()

const steps = [
  {
    title: 'Import a document',
    body: 'Drag a PDF or image into SnipCell, or use Import to browse from your computer.',
    img: '/assets/getting-started-import.png',
  },
  {
    title: 'Open it next to Excel',
    body: 'Your imported files appear in the document tree. Select a page to view it while you keep working in Excel.',
    img: '/assets/getting-started-documents.png',
  },
  {
    title: 'Choose a Snip mode',
    body: 'Use the toolbar — or right-click the page — to choose Text, Sum, Table, Validation, or Exception. (Number keys 1–5 work too.)',
    img: '/assets/getting-started-modes.png',
  },
  {
    title: 'Snip into the selected cell',
    body: 'Draw a box on the document and SnipCell places the result in the active Excel cell. Linked areas stay highlighted, so clicking the Excel cell takes you back to the source, and vice versa.',
    img: '/assets/getting-started-snip-to-excel.png',
  },
  {
    title: 'Add structure and notes',
    body: 'Use Table snip to adjust columns before sending data to Excel, and add comments to your snippets to collaborate.',
    img: '/assets/getting-started-table-comments.png',
  },
]

const i = ref(0)
function next() {
  if (i.value < steps.length - 1) i.value++
  else emit('close')
}
function back() {
  if (i.value > 0) i.value--
}
</script>

<template>
  <div class="overlay" @click.self="emit('close')">
    <div class="wiz">
      <button class="skip" @click="emit('close')">Skip</button>
      <img :src="steps[i].img" class="img" alt="" />
      <h2>{{ steps[i].title }}</h2>
      <p>{{ steps[i].body }}</p>
      <div class="dots">
        <span v-for="(s, n) in steps" :key="n" :class="{ on: n === i }"></span>
      </div>
      <div class="nav">
        <button :disabled="i === 0" @click="back">Back</button>
        <button class="primary" @click="next">
          {{ i === steps.length - 1 ? "Let's get started" : 'Next' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: grid;
  place-items: center;
  z-index: 60;
  padding: 16px;
}
.wiz {
  position: relative;
  background: var(--bg);
  border-radius: 10px;
  width: 100%;
  max-width: 340px;
  padding: 18px;
  text-align: center;
  box-shadow: 0 10px 36px rgba(0, 0, 0, 0.35);
}
.skip {
  position: absolute;
  top: 8px;
  right: 8px;
  border: none;
  background: none;
  color: var(--muted);
  font-size: 12px;
}
.img {
  width: 100%;
  border-radius: 8px;
  border: 1px solid var(--line);
  margin-bottom: 12px;
  object-fit: cover;
  max-height: 180px;
}
h2 { font-size: 16px; margin: 0 0 6px; }
p { color: var(--muted); font-size: 13px; margin: 0 0 14px; }
.dots { display: flex; justify-content: center; gap: 6px; margin-bottom: 14px; }
.dots span { width: 7px; height: 7px; border-radius: 50%; background: var(--line); }
.dots span.on { background: var(--brand); }
.nav { display: flex; gap: 8px; justify-content: space-between; }
.nav button { flex: 1; }
</style>
